import { FC, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as FileIcon } from '@/assets/icons/menu/download-file.svg';
import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import BackButton from '@/components/BackButton';
import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { EditorialFacebookPostNetwork } from '@/utils/enums';
import { download } from '@/utils/helpers';
import { scrollBarStyles } from '@/utils/theme';

import GroupsTable from './GroupsTable';
import PagesTable from './PagesTable';
import PinterestBoardsTable from './PinterestBoardsTable';

const Dialog = styled(StyledDialog)`
  width: 96%;
  max-width: 1160px;
  text-align: left;
  padding: 0;
`;

const Heading = styled(Text)`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  box-shadow: 0px 1px 0px 0px rgb(34, 34, 34, 0.08);
`;

const Footer = styled.div`
  padding: 24px 24px 32px;
  box-shadow: 0px -1px 8px 0px #2222220f;
  text-align: right;
`;

const Content = styled.div`
  margin-top: 1px;
  padding: 24px;
  padding-bottom: 0;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

const TableHolder = styled.div`
  height: calc(72vh - 212px);
  overflow-y: auto;
  ${scrollBarStyles};
`;

const ControlsHolder = styled.div`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledButton = styled(Button)`
  padding: 0 16px;
  color: ${({ theme }) => theme.colors.gray};
  &:hover,
  &:active {
    color: ${({ theme }) => theme.colors.black};
  }
`;

const Downloadables = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

interface Props {
  onClose: VoidFunction;
}

const PagesAndGroupsInfoModal: FC<Props> = ({ onClose }) => {
  const [type, setType] = useState<'pages' | 'groups'>('pages');
  const [activePageId, setActivePageId] = useState<number | null>(null);

  const [defaultNetwork] = useNetwork();

  const [network, setNetwork] =
    useState<EditorialFacebookPostNetwork>(defaultNetwork);

  const alerts = useAlerts();

  const fetchPagesCsv = () =>
    api.get<never, string>('/v1/editorial/queue/channels/export', {
      params: {
        ...(network === EditorialFacebookPostNetwork.INSTAGRAM && {
          'filter[hasInstagram]': true,
        }),
        ...(network === EditorialFacebookPostNetwork.THREADS && {
          'filter[hasThreads]': true,
        }),
        ...(network === EditorialFacebookPostNetwork.PINTEREST && {
          'filter[hasPinterest]': true,
        }),
      },
    });

  const fetchPageGroupsCsv = () =>
    api.get<never, string>('/v1/editorial/queue/channel-groups/export');

  const onExport = async (
    fileName = 'export.csv',
    fetchCsv: () => Promise<string>,
  ) => {
    try {
      const csv = await fetchCsv();

      download(csv, fileName);

      alerts.close();
    } catch (err) {
      alerts.error();
    }
  };

  const showPinterestBoards = (channelId: number) => {
    setActivePageId(channelId);
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Heading $type="button">Pages, Groups & Boards IDs</Heading>
        <CloseButton onClick={onClose}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Content>
          {activePageId ? (
            <BackButton
              onClick={() => {
                setActivePageId(null);
              }}
            />
          ) : (
            <ControlsHolder>
              <Controls>
                <ControlsButton
                  active={network === EditorialFacebookPostNetwork.FACEBOOK}
                  onClick={() =>
                    setNetwork(EditorialFacebookPostNetwork.FACEBOOK)
                  }
                >
                  Facebook
                </ControlsButton>
                <ControlsButton
                  active={network === EditorialFacebookPostNetwork.INSTAGRAM}
                  onClick={() => {
                    setNetwork(EditorialFacebookPostNetwork.INSTAGRAM);
                    setType('pages');
                  }}
                >
                  Instagram
                </ControlsButton>
                <ControlsButton
                  active={network === EditorialFacebookPostNetwork.THREADS}
                  onClick={() => {
                    setNetwork(EditorialFacebookPostNetwork.THREADS);
                    setType('pages');
                  }}
                >
                  Threads
                </ControlsButton>
                <ControlsButton
                  active={network === EditorialFacebookPostNetwork.PINTEREST}
                  onClick={() => {
                    setNetwork(EditorialFacebookPostNetwork.PINTEREST);
                    setType('pages');
                  }}
                >
                  Pinterest
                </ControlsButton>
              </Controls>
              {network === EditorialFacebookPostNetwork.FACEBOOK && (
                <>
                  <Controls>
                    <ControlsButton
                      active={type === 'pages'}
                      onClick={() => setType('pages')}
                    >
                      Pages
                    </ControlsButton>
                    <ControlsButton
                      active={type === 'groups'}
                      onClick={() => {
                        setType('groups');
                        setNetwork(EditorialFacebookPostNetwork.FACEBOOK);
                      }}
                    >
                      Groups
                    </ControlsButton>
                  </Controls>
                </>
              )}
              <Downloadables>
                <StyledButton
                  color="secondary"
                  small
                  onClick={() => onExport('pages_ids.csv', fetchPagesCsv)}
                >
                  <FileIcon />
                  Download Page IDs
                </StyledButton>
                {network === EditorialFacebookPostNetwork.FACEBOOK && (
                  <StyledButton
                    color="secondary"
                    small
                    onClick={() =>
                      onExport('page_groupd_ids.csv', fetchPageGroupsCsv)
                    }
                  >
                    <FileIcon />
                    Download Group IDs
                  </StyledButton>
                )}
              </Downloadables>
            </ControlsHolder>
          )}
          <TableHolder>
            {activePageId ? (
              <PinterestBoardsTable channelId={activePageId} />
            ) : (
              <>
                {type === 'pages' ? (
                  <PagesTable
                    network={network}
                    showPinterestBoards={showPinterestBoards}
                  />
                ) : (
                  <GroupsTable />
                )}
              </>
            )}
          </TableHolder>
        </Content>
        <Footer>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default PagesAndGroupsInfoModal;
